import type { Patient, Reference, Resource } from '@medplum/fhirtypes'
import { Assessment } from '@seen/core/models/assessments/assessment'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { createReference } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { useResource } from '#src/hooks/useResource.ts'
import { useWarningReporter } from '#src/hooks/useWarningReporter.ts'

// TODO: Combine with useTimelineItemUrl
export function useCommentRelatedParticipant(
  comment: CommentCommunication | undefined
): Reference<Patient> | undefined {
  useWarningReporter(
    !!comment?.partOf &&
      comment.partOf.length !== 1 &&
      'Expected exactly one partOf reference'
  )
  const { data: parentCommentOrResource } = useResource(comment?.partOf?.[0])
  useWarningReporter(
    CommentCommunication.isInstance(parentCommentOrResource) &&
      !!parentCommentOrResource?.partOf &&
      parentCommentOrResource.partOf.length !== 1 &&
      'Expected exactly one partOf reference for parent comment'
  )
  const { data: maybeResource } = useResource(
    CommentCommunication.isInstance(parentCommentOrResource)
      ? parentCommentOrResource.partOf?.[0]
      : undefined
  )
  const resource = maybeResource ?? parentCommentOrResource

  const participantReference = getParticipantReference(resource)
  return participantReference
}

function getParticipantReference(
  resource: Resource | undefined
): Reference<Patient> | undefined {
  if (Assessment.isInstance(resource)) {
    const assessment = Assessment.fromFhirResource(resource)
    return assessment.subject
  }

  if (StandardTask.isInstance(resource)) {
    const task = StandardTask.fromFhirResource(resource)
    return task.participant
  }

  if (isOfType(resource, 'Patient')) {
    return createReference(resource)
  }

  return undefined
}
